import React from "react";
import clsx from "clsx";

/**
 * Button - Reusable button component
 *
 * Variants:
 * - primary: Orange gradient button for main actions (brand color)
 * - secondary: White button with border for secondary actions
 * - ghost: Transparent button for subtle actions
 * - destructive: Red button for dangerous actions
 */
const Button = ({
  children,
  onClick,
  type = "button",
  variant = "primary",
  size = "md",
  disabled = false,
  fullWidth = false,
  className = "",
  icon: Icon,
  iconPosition = "left",
}) => {
  const baseStyles =
    "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-150 focus:outline-none";

  const sizeStyles = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
  };

  // Primary variant uses inline styles for gradient
  const isPrimary = variant === "primary";

  const variantStyles = {
    primary: "", // Handled by inline styles
    secondary:
      "border border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]",
    ghost:
      "bg-transparent text-[#6B7280] hover:text-[#E07A3A] hover:bg-[#FEF3E7]",
    destructive:
      "bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-400",
  };

  const primaryStyle = isPrimary
    ? disabled
      ? {
          background: "#F3F4F6",
          color: "#9CA3AF",
          cursor: "not-allowed",
        }
      : {
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
          color: "white",
          boxShadow: "0 1px 3px rgba(224, 122, 58, 0.3)",
        }
    : {};

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && "w-full",
        disabled && !isPrimary && "cursor-not-allowed opacity-50",
        className
      )}
      style={primaryStyle}
      onMouseEnter={(e) => {
        if (isPrimary && !disabled) {
          e.currentTarget.style.boxShadow = "0 2px 6px rgba(224, 122, 58, 0.4)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }
      }}
      onMouseLeave={(e) => {
        if (isPrimary && !disabled) {
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(224, 122, 58, 0.3)";
          e.currentTarget.style.transform = "translateY(0)";
        }
      }}
    >
      {Icon && iconPosition === "left" && (
        <Icon className="mr-2 h-4 w-4 shrink-0" />
      )}
      {children}
      {Icon && iconPosition === "right" && (
        <Icon className="ml-2 h-4 w-4 shrink-0" />
      )}
    </button>
  );
};

export default Button;
